import React from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, Pressable, Text } from '@gluestack-ui/themed';

import type { GetMetricsGated } from '@/common/api/models/getMetricsGated';

export type SegmentedFilterProps = {
  value: GetMetricsGated;
  onChange: (value: GetMetricsGated) => void;
  disabled?: boolean;
};

/**
 * Gated / Ungated segmented control for metrics report filters (32px track).
 */
export function SegmentedFilter({ value, onChange, disabled = false }: SegmentedFilterProps) {
  const { t } = useTranslation();

  const options: { label: string; value: GetMetricsGated; testID: string }[] = [
    {
      label: t('admin.reports.filters.gated'),
      value: 'gated',
      testID: 'report-filter-gated-option',
    },
    {
      label: t('admin.reports.filters.ungated'),
      value: 'ungated',
      testID: 'report-filter-ungated-option',
    },
  ];

  return (
    <HStack
      testID="report-filter-gated"
      bg="$bgSubtle"
      height={32}
      p={2}
      borderRadius="$md"
      alignSelf="flex-start"
      flexShrink={0}
      borderWidth={1}
      borderColor="$borderLight"
      alignItems="center"
      sx={{ _web: { width: 'max-content' } }}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <Pressable
            key={option.value}
            onPress={() => {
              if (!disabled) onChange(option.value);
            }}
            height={28}
            px="$3"
            borderRadius="$sm"
            bg={isActive ? '$white' : 'transparent'}
            opacity={disabled ? 0.6 : 1}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            testID={option.testID}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive, disabled }}
            sx={{ _web: { flexShrink: 0, width: 'max-content' } }}
          >
            <Text
              size="xs"
              color={isActive ? '$textHeading' : '$textSubtle'}
              fontFamily={isActive ? '$heading' : undefined}
              flexShrink={0}
              sx={{ _web: { whiteSpace: 'nowrap' } }}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </HStack>
  );
}
